import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import SignaturePad from "@/components/SignaturePad";
import { ArrowLeft, CheckCircle2, Circle } from "lucide-react";
import { REVIEW } from "@/constants/testIds";



const STAGES_ORDER = ["pending_employee", "pending_manager", "pending_final", "completed"];
const STAGE_LABELS = {
  pending_employee: "Employee self-review",
  pending_manager: "Manager review",
  pending_final: "Combined session",
  completed: "Completed",
};

export default function ReviewWorkflow() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [review, setReview] = useState(null);
  const [users, setUsers] = useState([]);
  const [bus, setBus] = useState([]);
  const [priorReviews, setPriorReviews] = useState([]);
  const [scores, setScores] = useState({});
  const [overallComment, setOverallComment] = useState("");
  const [signature, setSignature] = useState(null);
  const [signatureEmp, setSignatureEmp] = useState(null);

  const load = async () => {
    const [r, u, b, all] = await Promise.all([
      api.get(`/reviews/${id}`),
      api.get("/users").catch(() => ({ data: [] })),
      api.get("/business-units").catch(() => ({ data: [] })),
      api.get("/reviews").catch(() => ({ data: [] })),
    ]);
    setReview(r.data); setUsers(u.data); setBus(b.data);
    // Same employee + same fiscal year + earlier quarter than the one being viewed.
    const curQ = r.data.quarter || "";
    const curFY = r.data.fiscal_year || r.data.template_snapshot?.fiscal_year;
    const order = { Q1: 1, Q2: 2, Q3: 3, Q4: 4 };
    const priors = (all.data || []).filter((rev) =>
      rev.id !== r.data.id
      && rev.employee_id === r.data.employee_id
      && (rev.fiscal_year || rev.template_snapshot?.fiscal_year) === curFY
      && order[rev.quarter] && order[curQ] && order[rev.quarter] < order[curQ]
    ).sort((a, b) => order[b.quarter] - order[a.quarter]); // newest-prior first (Q3 then Q2 then Q1)
    setPriorReviews(priors);
  };
  useEffect(() => { load(); }, [id]);

  const rows = review?.template_snapshot?.rows || [];
  const isSuper = user?.role === "super_admin";
  const isManager = user?.id === review?.manager_id || isSuper;
  const isEmployee = user?.id === review?.employee_id || isSuper;

  const onScoreChange = (rowId, patch) => setScores((s) => ({ ...s, [rowId]: { ...s[rowId], row_id: rowId, ...patch } }));

  const computeTotal = (scoreMap) => {
    let t = 0;
    rows.forEach((r) => {
      const s = scoreMap[r.id];
      if (s && s.rating != null) t += Number(s.rating) * (Number(r.weight) / 100);
    });
    return Math.round(t * 100) / 100;
  };

  const submitStage = async (endpoint, payload) => {
    try {
      const { data } = await api.post(`/reviews/${id}/${endpoint}`, payload);
      setReview(data); setScores({}); setOverallComment(""); setSignature(null); setSignatureEmp(null);
      toast.success("Submitted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!review) return <div className="text-sm text-zinc-500">Loading…</div>;

  const uname = (id) => users.find((u) => u.id === id)?.name || id;

  // Stage UI
  const stageIdx = STAGES_ORDER.indexOf(review.stage);

  const stageView = (() => {
    if (review.stage === "pending_employee" && isEmployee) {
      return {
        title: "Employee self-review",
        helper: "Score each KPA from 0–5 based on your own assessment. Once you sign and submit, your manager will be notified to score you. Your self-scores are locked after submission.",
        onSubmit: () => {
          if (!signature?.value) return toast.error("Please sign before submitting");
          submitStage("employee-submit", { scores: rows.map((r) => scores[r.id]).filter(Boolean), signature, overall_comment: overallComment });
        },
        submitTestId: REVIEW.submitEmployeeBtn,
        editable: true,
        sigLabel: "Employee signature",
      };
    }
    if (review.stage === "pending_manager" && isManager) {
      return {
        title: "Manager review",
        helper: "The employee has submitted their self-scores. Enter your own manager rating for each KPA, add a comment and sign to submit. A combined session will follow.",
        onSubmit: () => {
          if (!signature?.value) return toast.error("Please sign before submitting");
          submitStage("manager-submit", { scores: rows.map((r) => scores[r.id]).filter(Boolean), signature, overall_comment: overallComment });
        },
        submitTestId: REVIEW.submitManagerBtn,
        editable: true,
        sigLabel: "Manager signature",
      };
    }
    if (review.stage === "pending_final" && (isManager || isSuper)) {
      return {
        title: "Combined session",
        helper: "Record the agreed final score per KPA. Both manager and employee sign live at the end of the session.",
        onSubmit: () => {
          if (!signature?.value || !signatureEmp?.value) return toast.error("Both manager and employee must sign");
          submitStage("final-submit", { scores: rows.map((r) => scores[r.id]).filter(Boolean), signature_manager: signature, signature_employee: signatureEmp, overall_comment: overallComment });
        },
        submitTestId: REVIEW.submitFinalBtn,
        editable: true,
        sigLabel: "Final signatures",
        finalMeeting: true,
      };
    }
    return null;
  })();

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => nav("/reviews")} className="text-sm text-zinc-600 hover:text-zinc-900 flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Reviews
        </button>
      </div>

      {/* Workflow timeline */}
      <div className="border border-zinc-200 bg-white px-6 py-5 mb-6">
        <div className="flex items-center justify-between">
          {STAGES_ORDER.map((stage, i) => {
            const done = i < stageIdx || (review.stage === "completed" && i <= stageIdx);
            const active = i === stageIdx && review.stage !== "completed";
            return (
              <div key={stage} className="flex items-center flex-1">
                <div className="flex items-center gap-2">
                  {done ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <Circle className={`w-5 h-5 ${active ? "text-zinc-900" : "text-zinc-300"}`} />}
                  <div className={`text-xs uppercase tracking-wider ${active ? "font-semibold text-zinc-900" : done ? "text-emerald-700" : "text-zinc-400"}`}>
                    {STAGE_LABELS[stage]}
                  </div>
                </div>
                {i < STAGES_ORDER.length - 1 && <div className={`flex-1 h-px mx-3 ${i < stageIdx ? "bg-emerald-300" : "bg-zinc-200"}`} />}
              </div>
            );
          })}
        </div>
      </div>

      {/* Document */}
      <div className="bg-white border border-zinc-300 mb-6">
        <div className="px-8 py-6 border-b-2 border-zinc-900 flex items-start gap-6">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{review.period_label}</div>
            <h1 className="text-2xl font-bold mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {review.template_snapshot.name}
            </h1>
            <div className="mt-3 grid grid-cols-4 gap-6 text-sm">
              <Meta label="Employee" value={uname(review.employee_id)} />
              <Meta label="Manager" value={uname(review.manager_id)} />
              <Meta label="Fiscal Year" value={review.template_snapshot.fiscal_year} />
              <Meta label="Stage" value={STAGE_LABELS[review.stage]} />
            </div>
          </div>
          {(() => {
            const bu = bus.find((b) => b.id === review.template_snapshot?.business_unit_id);
            return bu?.logo ? (
              <img
                src={bu.logo}
                alt={`${bu.name} logo`}
                data-testid="review-bu-logo"
                className="h-16 w-auto max-w-[160px] object-contain flex-shrink-0"
              />
            ) : null;
          })()}
        </div>

        <div className="px-8 py-6 overflow-x-auto">
          <table className="w-full border-collapse border-2 border-zinc-900 text-sm">
            <thead>
              <tr className="bg-zinc-900 text-white text-[10px] uppercase tracking-wider">
                <th className="border border-zinc-900 px-2 py-2 text-left w-36">Perspective</th>
                <th className="border border-zinc-900 px-2 py-2 text-left">KPA</th>
                <th className="border border-zinc-900 px-2 py-2 text-center w-16">Wt %</th>
                <th className="border border-zinc-900 px-2 py-2 text-center w-24">Emp Rating</th>
                <th className="border border-zinc-900 px-2 py-2 text-center w-24">Mgr Rating</th>
                <th className="border border-zinc-900 px-2 py-2 text-center w-24">Final</th>
                <th className="border border-zinc-900 px-2 py-2 text-center w-20">Score</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const mgr = (review.manager_scores || []).find((x) => x.row_id === r.id);
                const emp = (review.employee_scores || []).find((x) => x.row_id === r.id);
                const fin = (review.final_scores || []).find((x) => x.row_id === r.id);

                const editableStage = stageView?.editable && (
                  (review.stage === "pending_manager" && isManager) ||
                  (review.stage === "pending_employee" && isEmployee) ||
                  (review.stage === "pending_final" && (isManager || isSuper))
                );
                const cur = scores[r.id];
                const baseRating = review.stage === "pending_final" ? (cur?.rating ?? fin?.rating) :
                                   review.stage === "pending_employee" ? (cur?.rating ?? emp?.rating) :
                                   (cur?.rating ?? mgr?.rating);
                const score = baseRating != null ? Math.round((baseRating * r.weight / 100) * 100) / 100 : null;

                return (
                  <React.Fragment key={r.id}>
                    <tr className="align-top">
                      <td className="border border-zinc-400 px-2 py-2 bg-zinc-50 font-medium">{r.perspective}</td>
                      <td className="border border-zinc-400 px-2 py-2">
                        <div className="font-medium">{r.kpa}</div>
                        {r.strategic_goal && r.strategic_goal !== r.kpa && (
                          <div className="text-xs text-zinc-500">{r.strategic_goal}</div>
                        )}
                        {r.measurement_tool && (
                          <div className="text-[11px] text-zinc-500 mt-1">Tool: {r.measurement_tool}</div>
                        )}
                        {(r.target || r.frequency || r.source_of_truth) && (
                          <div className="mt-2 grid grid-cols-1 gap-0.5 text-[11px] border-t border-zinc-200 pt-1.5">
                            {r.target && <div><span className="text-zinc-500">Target: </span><span className="text-zinc-800">{r.target}</span></div>}
                            {r.frequency && <div><span className="text-zinc-500">Frequency: </span><span className="text-zinc-800">{r.frequency}</span></div>}
                            {r.source_of_truth && <div><span className="text-zinc-500">Source of truth: </span><span className="text-zinc-800">{r.source_of_truth}</span></div>}
                          </div>
                        )}
                      </td>
                      <td className="border border-zinc-400 px-2 py-2 text-center tabular-nums">{r.weight}%</td>

                      <td className="border border-zinc-400 px-2 py-2 text-center tabular-nums">
                        {review.stage === "pending_employee" && isEmployee
                          ? <ScoreInput value={cur?.rating ?? ""} onChange={(v) => onScoreChange(r.id, { rating: v })} testId={`emp-rating-${r.id}`} />
                          : (emp?.rating ?? "—")}
                      </td>
                      <td className="border border-zinc-400 px-2 py-2 text-center tabular-nums">
                        {review.stage === "pending_manager" && isManager
                          ? <ScoreInput value={cur?.rating ?? ""} onChange={(v) => onScoreChange(r.id, { rating: v })} testId={`mgr-rating-${r.id}`} />
                          : (mgr?.rating ?? "—")}
                      </td>
                      <td className="border border-zinc-400 px-2 py-2 text-center tabular-nums">
                        {review.stage === "pending_final" && (isManager || isSuper)
                          ? <ScoreInput value={cur?.rating ?? ""} onChange={(v) => onScoreChange(r.id, { rating: v })} testId={`final-rating-${r.id}`} />
                          : (fin?.rating ?? "—")}
                      </td>
                      <td className="border border-zinc-400 px-2 py-2 text-center tabular-nums font-semibold">
                        {score != null ? score : "—"}
                      </td>
                    </tr>
                    {editableStage && (
                      <tr>
                        <td colSpan={7} className="border border-zinc-400 border-t-0 px-2 py-1 bg-zinc-50">
                          <input
                            placeholder="Comment for this KPA…"
                            className="w-full text-xs px-2 py-1 border border-zinc-200 rounded-sm bg-white"
                            value={cur?.comment || ""}
                            onChange={(e) => onScoreChange(r.id, { comment: e.target.value, rating: cur?.rating ?? 0 })}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              <tr className="bg-zinc-100 font-semibold">
                <td colSpan={6} className="border border-zinc-400 px-2 py-2 text-right">Final KPA Score</td>
                <td className="border border-zinc-400 px-2 py-2 text-center tabular-nums">
                  {review.final_total ?? (Object.keys(scores).length ? computeTotal(scores) : "—")}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Existing signatures */}
        <div className="px-8 pb-8 grid grid-cols-3 gap-6">
          <SignatureView label="Employee" value={review.final_signature_employee || review.employee_signature} date={review.employee_submitted_at} />
          <SignatureView label="Manager" value={review.final_signature_manager || review.manager_signature} date={review.manager_submitted_at} />
          <SignatureView label="Combined session" value={review.final_signature_manager} date={review.final_submitted_at} />
        </div>
      </div>

      {/* Action panel */}
      {stageView ? (
        <div className="border border-zinc-200 bg-white p-6">
          <h2 className="text-xl font-semibold mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{stageView.title}</h2>
          <p className="text-sm text-zinc-500 mb-5">{stageView.helper}</p>

          <div className="mb-5">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Overall comment</label>
            <textarea rows={3} className="w-full mt-1 border border-zinc-300 px-3 py-2 rounded-sm text-sm" value={overallComment} onChange={(e) => setOverallComment(e.target.value)} placeholder="Add any overall feedback or notes" />
          </div>

          {stageView.finalMeeting ? (
            <div className="grid grid-cols-2 gap-4">
              <SignaturePad key={`mgr-${review.stage}`} label="Manager signature" value={signature} onChange={setSignature} />
              <SignaturePad key={`emp-${review.stage}`} label="Employee signature" value={signatureEmp} onChange={setSignatureEmp} />
            </div>
          ) : (
            <SignaturePad key={`sig-${review.stage}`} label={stageView.sigLabel} value={signature} onChange={setSignature} />
          )}

          <div className="mt-5 flex justify-end">
            <button onClick={stageView.onSubmit} data-testid={stageView.submitTestId} className="bg-zinc-900 text-white px-5 py-2.5 rounded-sm text-sm font-medium hover:bg-zinc-800">
              Submit & advance
            </button>
          </div>
        </div>
      ) : review.stage === "completed" ? (
        <div className="border border-emerald-300 bg-emerald-50 text-emerald-900 px-5 py-4 rounded-sm">
          <div className="font-semibold">Review completed</div>
          <div className="text-sm">Final KPA score: <b>{review.final_total}</b></div>
        </div>
      ) : (
        <div className="border border-zinc-300 bg-zinc-50 text-zinc-700 px-5 py-4 rounded-sm text-sm">
          Awaiting the next participant. You don&apos;t have a pending action on this review.
        </div>
      )}

      {/* Prior quarters — read-only, newest first */}
      {priorReviews.length > 0 && (
        <div className="mt-10">
          <div className="flex items-center gap-2 mb-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">History</div>
            <div className="h-px bg-zinc-200 flex-1" />
            <div className="text-[10px] uppercase tracking-wider text-zinc-400">
              {priorReviews.length} prior quarter{priorReviews.length === 1 ? "" : "s"} for {uname(review.employee_id)}
            </div>
          </div>
          {priorReviews.map((pr) => (
            <PriorQuarterCard key={pr.id} review={pr} users={users} />
          ))}
        </div>
      )}
    </div>
  );
}

function PriorQuarterCard({ review, users }) {
  const [open, setOpen] = useState(true); // expanded by default — user wants to see them
  const rows = review.template_snapshot?.rows || [];
  const mgrMap = Object.fromEntries((review.manager_scores || []).map((s) => [s.row_id, s]));
  const empMap = Object.fromEntries((review.employee_scores || []).map((s) => [s.row_id, s]));
  const finMap = Object.fromEntries((review.final_scores || []).map((s) => [s.row_id, s]));
  const uname = (id) => users.find((u) => u.id === id)?.name || id;
  const stageDone = review.stage === "completed";
  return (
    <div className="bg-white border border-zinc-200 mb-3" data-testid={`prior-quarter-${review.quarter}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-5 py-3 flex items-center gap-3 border-b border-zinc-200 hover:bg-zinc-50"
      >
        <span className="text-sm font-semibold tabular-nums">{review.quarter}</span>
        <span className="text-xs text-zinc-500">·</span>
        <span className="text-xs text-zinc-500">{review.fiscal_year || review.template_snapshot?.fiscal_year}</span>
        <span className="text-xs text-zinc-500">·</span>
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${stageDone ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-800"}`}>
          {review.stage.replace("_", " ")}
        </span>
        <span className="flex-1 text-right text-xs text-zinc-500">
          Final: <b className="tabular-nums text-zinc-900">{review.final_total ?? "—"}</b>
        </span>
        <span className="text-xs text-zinc-400">{open ? "Hide ▴" : "Show ▾"}</span>
      </button>
      {open && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" style={{ tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: "12%" }} />
              <col style={{ width: "26%" }} />
              <col style={{ width: "68px" }} />
              <col style={{ width: "70px" }} />
              <col style={{ width: "70px" }} />
              <col style={{ width: "70px" }} />
              <col />
            </colgroup>
            <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2">Perspective</th>
                <th className="text-left px-3 py-2">KPA</th>
                <th className="text-right px-3 py-2">Wt %</th>
                <th className="text-center px-3 py-2">Emp</th>
                <th className="text-center px-3 py-2">Mgr</th>
                <th className="text-center px-3 py-2">Final</th>
                <th className="text-left px-3 py-2">Comments</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const m = mgrMap[r.id];
                const e = empMap[r.id];
                const f = finMap[r.id];
                return (
                  <tr key={r.id} className="border-t border-zinc-100 align-top">
                    <td className="px-3 py-2 text-zinc-700">{r.perspective || "—"}</td>
                    <td className="px-3 py-2 text-zinc-700 whitespace-normal break-words">{r.kpa || r.strategic_goal || "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.weight}</td>
                    <td className="px-3 py-2 text-center tabular-nums">{e?.rating ?? "—"}</td>
                    <td className="px-3 py-2 text-center tabular-nums">{m?.rating ?? "—"}</td>
                    <td className="px-3 py-2 text-center tabular-nums font-semibold">{f?.rating ?? "—"}</td>
                    <td className="px-3 py-2 text-zinc-600 whitespace-normal break-words">{f?.comment || m?.comment || e?.comment || ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="px-5 py-3 border-t border-zinc-100 text-xs text-zinc-500 flex items-center gap-6">
            <span>Manager: <b className="text-zinc-700">{uname(review.manager_id)}</b></span>
            <span>Employee: <b className="text-zinc-700">{uname(review.employee_id)}</b></span>
            {review.completed_at && <span>Completed: {new Date(review.completed_at).toLocaleDateString()}</span>}
            <Link to={`/reviews/${review.id}`} className="ml-auto text-blue-600 hover:underline">Open full review →</Link>
          </div>
        </div>
      )}
    </div>
  );
}

function ScoreInput({ value, onChange, testId }) {
  return (
    <select value={value === "" ? "" : String(value)} onChange={(e) => onChange(Number(e.target.value))} data-testid={testId} className="w-16 border border-zinc-300 px-1 py-1 rounded-sm bg-white text-center">
      <option value="">—</option>
      {[0, 1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
    </select>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-sm font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function SignatureView({ label, value, date }) {
  return (
    <div className="border-t border-zinc-300 pt-2">
      <div className="h-14 flex items-end">
        {value?.mode === "type" && <div className="font-serif italic text-2xl">{value.value}</div>}
        {value?.mode === "draw" && value.value && <img src={value.value} alt="" className="max-h-14" />}
        {value?.mode === "upload" && value.value && <img src={value.value} alt="" className="max-h-14" />}
        {!value?.value && <span className="text-zinc-400 text-xs">— not signed —</span>}
      </div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">{label} · {date ? new Date(date).toLocaleDateString() : "—"}</div>
    </div>
  );
}
